import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { tableStandardProps } from "@/utils/componentUtils";
import { getActiveEnvProfile } from "@/utils/utils";
import { Space, Switch, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useGetNotificationSettings, useUpdateNotificationSettings } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { useIsSystemAdmin } from "../../../auth/hooks";
import { NotificationTopicParams } from "../../enums";
import { NotificationSettings } from "../../types";

const NotificationSettingsView = () => {
  const { t } = useTranslation();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();

  const { data } = useGetNotificationSettings();
  const updateNotificationSettings = useUpdateNotificationSettings();

  const handleSubscriptionChange = (notification: NotificationSettings, subscribed: boolean) => {
    updateNotificationSettings.mutate([
      {
        optimisticLockVersion: notification.optimisticLockVersion,
        topic: notification.topic,
        subscribed,
        sendAsEmail: !subscribed ? false : notification.sendAsEmail,
        sendAsSms: notification.sendAsSms,
      },
    ]);
  };

  const handleSendAsEmailChange = (notification: NotificationSettings, sendAsEmail: boolean) => {
    updateNotificationSettings.mutate([
      {
        optimisticLockVersion: notification.optimisticLockVersion,
        topic: notification.topic,
        subscribed: notification.subscribed,
        sendAsEmail,
        sendAsSms: notification.sendAsSms,
      },
    ]);
  };

  const columns: ColumnsType<NotificationSettings> = [
    {
      key: "topic",
      title: t("notification.enums.notificationTopic._label"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("notification.enums.notificationTopic." + record.topic)}</Ellipsis>,
    },
    {
      key: "subscription",
      title: (
        <LabelWithTooltip
          label={t("notification.helpers.subscriptionActive")}
          tooltip={t("notification.helpers.subscriptionActiveDesc")}
        />
      ),
      width: 155,
      render: (_, record) => (
        <Space size="small">
          <Switch
            size="small"
            checked={record.subscribed}
            disabled={NotificationTopicParams.get(record.topic)?.mandatory}
            checkedChildren={<AntIcon type="check" />}
            unCheckedChildren={<AntIcon type="close" />}
            onChange={(checked) => handleSubscriptionChange(record, checked)}
          />
          {NotificationTopicParams.get(record.topic)?.mandatory && (
            <LabelWithTooltip tooltip={t("notification.helpers.subscriptionDisabledDesc")} />
          )}
        </Space>
      ),
    },
    {
      key: "sendAsEmail",
      title: (
        <LabelWithTooltip
          label={t("notification.helpers.sendAsEmail")}
          tooltip={t("notification.helpers.sendAsEmailDesc")}
        />
      ),
      width: 155,
      render: (_, record) => (
        <Space size="small">
          <Switch
            size="small"
            checked={record.sendAsEmail}
            disabled={
              !NotificationTopicParams.get(record.topic)?.emailSendable ||
              (NotificationTopicParams.get(record.topic)?.emailSendable && !record.subscribed) ||
              (NotificationTopicParams.get(record.topic)?.emailSendableMandatory &&
                !isCurrentUserSystemAdmin &&
                getActiveEnvProfile() === "prod")
            }
            checkedChildren={<AntIcon type="check" />}
            unCheckedChildren={<AntIcon type="close" />}
            onChange={(checked) => handleSendAsEmailChange(record, checked)}
          />
          {!NotificationTopicParams.get(record.topic)?.emailSendable && (
            <LabelWithTooltip tooltip={t("notification.helpers.sendAsEmailDisabledDesc")} />
          )}
          {NotificationTopicParams.get(record.topic)?.emailSendable && !record.subscribed && (
            <LabelWithTooltip tooltip={t("notification.helpers.sendAsEmailSubscriptionInactiveDesc")} />
          )}
          {NotificationTopicParams.get(record.topic)?.emailSendableMandatory &&
            !isCurrentUserSystemAdmin &&
            getActiveEnvProfile() === "prod" && (
              <LabelWithTooltip tooltip={t("notification.helpers.sendAsEmailMandatoryDesc")} />
            )}
        </Space>
      ),
    },
  ];

  return (
    <Table<NotificationSettings>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.SMALL }}
      dataSource={data?.filter((settings) => settings.available)}
      pagination={false}
    />
  );
};

export default NotificationSettingsView;
